// src/pages/AdminEducationPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { 
  getEducation, 
  createEducation, 
  updateEducation, 
  deleteEducation, 
  Education 
} from '../api/educationService';
import './AdminEducationPage.css';

const AdminEducationPage: React.FC = () => {
  const { user } = useAuth();
  const [educations, setEducation] = useState<Education[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [open, setOpen] = useState(false);
  const [currentEducation, setCurrentEducation] = useState<Partial<Education>>({});
  const [isEditing, setIsEditing] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [educationToDelete, setEducationToDelete] = useState<number | null>(null);

  // Fetch educations
  const fetchEducation = async () => {
    try {
      setLoading(true);
      const data = await getEducation();
      setEducation(data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching educations:', err);
      setError(`Failed to load educations: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEducation();
  }, []);

  // Handle dialog open for create/edit
  const handleOpenDialog = (education?: Education) => {
    if (education) {
      setCurrentEducation(education);
      setIsEditing(true);
    } else {
      setCurrentEducation({
        year_start: new Date().getFullYear(),
        year_end: new Date().getFullYear(),
        degree: '',
        specialization: '',
        university: '',
        location: '', 
        description: ''
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentEducation({});
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setCurrentEducation({
      ...currentEducation,
      [name]: name === 'year_start' || name === 'year_end' ? parseInt(value) || '' : value
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (isEditing && currentEducation.id) {
        await updateEducation(currentEducation.id, currentEducation);
        setSuccess('Education updated successfully');
      } else {
        await createEducation(currentEducation as Omit<Education, 'id'>);
        setSuccess('Education created successfully');
      }
      handleCloseDialog();
      fetchEducation();
    } catch (err: any) {
      setError(`Failed to save education: ${err.message}`);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    setEducationToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (educationToDelete) {
      try {
        await deleteEducation(educationToDelete);
        setSuccess('Education deleted successfully');
        fetchEducation();
      } catch (err: any) {
        setError(`Failed to delete education: ${err.message}`);
      }
    }
    setDeleteDialogOpen(false);
    setEducationToDelete(null);
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Check if user can edit (admin or editor)
  const canEdit = user && ['admin', 'editor'].includes(user.role);
  
  // Check if user can delete (admin only)
  const canDelete = user && user.role === 'admin';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Education
          </Typography>
          
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Education
            </Button>
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Degree</TableCell>
                  <TableCell>Specialization</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {educations.map((educ) => (
                  <TableRow key={educ.id}>
                    <TableCell>{educ.degree}</TableCell>
                    <TableCell>{educ.specialization}</TableCell>
                    <TableCell>
                      {educ.year_start} - {educ.year_end || 'Present'}
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <IconButton onClick={() => handleOpenDialog(educ)} color="primary">
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton onClick={() => handleDelete(educ.id!)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {educations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No educations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Education' : 'Add Education'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            label="Degree"
            name="degree"
            fullWidth
            value={currentEducation.degree || ''}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="normal"
            label="Specialization"
            name="specialization"
            fullWidth
            value={currentEducation.specialization || ''}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="normal"
            label="University"
            name="university"
            fullWidth
            value={currentEducation.university || ''}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="normal"
            label="Location"
            name="location"
            fullWidth
            value={currentEducation.location || ''}
            onChange={handleInputChange}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              margin="normal"
              label="Start Year"
              name="year_start"
              type="number"
              fullWidth
              value={currentEducation.year_start || ''}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="normal"
              label="End Year"
              name="year_end"
              type="number"
              fullWidth
              value={currentEducation.year_end || ''}
              onChange={handleInputChange}
              helperText="Leave empty for ongoing education"
            />
          </Box>
          <TextField
            margin="normal"
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={4}
            value={currentEducation.description || ''}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this education? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminEducationPage;
